import React, { Fragment } from 'react';
import FormattedDateTime from '../../../common/datetime/FormattedDateTime';
import {
  ApiAssociationDetails,
  ApiSanctionRecord,
  ApiSanctionRecordRole,
  ApiScreeningHitAiInsight,
} from '../../../api';
import { parseJsonSilent } from '../../../common/helper';
import { ContentLinkify } from '../../../common/ContentLinkify';
import { getHighlightedValue } from '../../../aml/sections/attributes/highlighters';
interface Props {
  index: number;
  highlightColor: string | undefined;
  highlight?: string;
  matchData: string;
  record?: ApiSanctionRecord;
  aiInsight?: ApiScreeningHitAiInsight;
}

const MatchExtraInformation = ({ highlight, matchData, record, highlightColor }: Props) => {
  const sanctionRecord = record;

  const displayYearWithOptionalParts = (year: string, month: string | undefined, day: string | undefined) => {
    if (month) {
      return day ? (
        <i>
          {year}-{month}-{day}
        </i>
      ) : (
        <i>
          {year}-{month}
        </i>
      );
    }
    return <i>{year}</i>;
  };

  function renderRole(role: ApiSanctionRecordRole) {
    return (
      <Fragment>
        <i>{role.type}</i>: {role.title}, {role.occupation}
        {role.sinceYear && (
          <span> - since: {displayYearWithOptionalParts(role.sinceYear, role.sinceMonth, role.sinceDay)}</span>
        )}
        {role.toYear && <span> - to: {displayYearWithOptionalParts(role.toYear, role.toMonth, role.toDay)}</span>}
      </Fragment>
    );
  }

  const displayRoles = (roles: Array<ApiSanctionRecordRole>) => {
    return (
      <div>
        <b>Roles:</b>
        {roles.map((role) => (
          <p key={role.title}>{renderRole(role)}</p>
        ))}
      </div>
    );
  };

  const displayAssociations = (associations: Array<ApiAssociationDetails>) => {
    return (
      <div>
        <b>Associations:</b>
        {associations.map((association) => (
          <div key={association.associateId} style={{ marginBottom: '1rem' }}>
            <i>{association.type}</i>: {association.ex && 'ex-'} {association.relationship} - {association.name}
            {association.roles.length > 0 &&
              association.roles.map((role) => (
                <p key={role.title} className='m-0 ps-3'>
                  {renderRole(role)}
                </p>
              ))}
          </div>
        ))}
      </div>
    );
  };

  if (sanctionRecord) {
    return (
      <Fragment>
        <ContentLinkify>
          <pre className='prewrap'>
            {getHighlightedValue({
              text: sanctionRecord.additionalInformation,
              highlight,
              color: highlightColor,
            })}
          </pre>
        </ContentLinkify>
        <p>
          <b>Last update by Dow Jones: </b>
          <FormattedDateTime date={sanctionRecord.recordDate} />
        </p>
        {sanctionRecord.roles && sanctionRecord.roles.length > 0 && displayRoles(sanctionRecord.roles)}
        {sanctionRecord.associations &&
          sanctionRecord.associations.length > 0 &&
          displayAssociations(sanctionRecord.associations)}
        {sanctionRecord.notes && (
          <div>
            <p className='mb-0'>
              <b>Notes:</b>
            </p>
            <div className='wrapped'>
              <ContentLinkify>{sanctionRecord.notes}</ContentLinkify>
            </div>
          </div>
        )}
      </Fragment>
    );
  } else {
    return (
      <>
        <ContentLinkify>
          <pre className='prewrap' data-testid={'non-record'}>
            {getHighlightedValue({
              text: JSON.stringify(parseJsonSilent(matchData).properties || {}, null, 2),
              highlight,
              color: highlightColor,
            })}
          </pre>
        </ContentLinkify>
      </>
    );
  }
};

export default MatchExtraInformation;
